import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { SkillService } from '../services/skill.service';
import { GlobalService } from 'services/global.service';

import usePersistantState from 'hooks/state.hook';

import { SKILL_COLUMN } from 'constants/import.constants';

import { Button, Client, CommonGlobalService, CommonUtilsService, DownloadIcon, RootState, Site, Skill, SkillFilter, UploadIcon, UUID } from '@umanforge/common';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { SearchInput } from '../components/inputs/search_input.component';
import { SkillComponent } from '../components/skills/skill.component';
import { SeeMore } from '../components/inputs/see_more.component';
import { Loading } from '../components/loading.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { PlusIcon } from '../components/icons/plus_icon.component';
import { MinusIcon } from '../components/icons/minus_icon.component';
import { SkillsIcon } from '../components/icons/skills_icon.component';
import { ExportModal } from 'features/import_export/components/modal_export.component';
import { ImportModal } from 'features/import_export/components/modal_import.component';
import { ImportHelperModal } from 'features/import_export/components/import_helper_modal.component';


export function SkillsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  /*----------------------------- useState ------------------------------*/
  const [ clients, setClients ] = useState<( Client & { isCollapsed: boolean } )[]>( [] );
  const [ sites, setSites ] = useState<Site[]>( [] );
  const [ skills, setSkills ] = useState<Skill[]>( [] );

  const [ filterName, setFilterName ] = usePersistantState<string>( 'skill_filterName', '' );
  const [ filterClients, setFilterClients ] = usePersistantState<UUID[]>( 'skill_filterClients', [] );
  const [ filterSites, setFilterSites ] = usePersistantState<UUID[]>( 'skill_filterSites', [] );

  const [ seeMoreClients, setSeeMoreClients ] = useState<boolean>( false );
  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isFilterEnd, setIsFilterEnd ] = useState<boolean>( false );

  const [ isOpenExportModal, setIsOpenExportModal ] = useState<boolean>( false );
  const [ isOpenImportModal, setIsOpenImportModal ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshFilters();
    }
  }, [ me ] );

  useEffect( () => {
    if ( isFilterEnd ) {
      checkClientFilter();
      checkSitesFilter();
      setTimeout( () => { setIsFirstRender( false ); }, 75 );
    }
  }, [ isFilterEnd ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshSkills();
    }
  }, [ isFirstRender, filterName, filterClients, filterSites ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSeeMoreClients( filterClients.length > 0 || filterSites.length > 0 );
    }
  }, [ isFirstRender, filterClients, filterSites ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshFilters = async (): Promise<void> => {
    const responseClients = ( await ClientService.getAllowedClients( null, token, dispatch ) ?? [] )
      .map( client => {
        const currentClient = clients.find( item => { return item.id === client.id; } );
        const isCollapsed = ( !( me?.isSuperAdmin == true ) || ( currentClient != null ? currentClient.isCollapsed : false ) );
        return { ...client, isCollapsed };
      } );
    const responseSites = await SiteService.getAllowedSites( null, token, dispatch ) ?? [];
    CommonUtilsService.sortByKey( responseClients, 'name', 'asc' );
    CommonUtilsService.sortByKey( responseSites, 'name', 'asc' );
    setClients( responseClients );
    setSites( responseSites );
    setIsFilterEnd( true );
  };

  const refreshSkills = async (): Promise<void> => {
    setIsLoading( true );
    const filters: SkillFilter = {
      filterName: filterName && filterName != '' ? filterName : undefined,
      filterClients: filterClients && filterClients.length > 0 ? filterClients : undefined,
      filterSites: filterSites && filterSites.length > 0 ? filterSites : undefined,
    };
    const responseSkills = await SkillService.getAllowSkills( filters, token, dispatch );
    responseSkills.sort( ( skill1, skill2 ) => {
      const site1 = sites.find( site => { return site.id === skill1.siteId; } );
      const site2 = sites.find( site => { return site.id === skill2.siteId; } );
      const client1 = clients.find( client => { return client.id === site1?.clientId; } );
      const client2 = clients.find( client => { return client.id === site2?.clientId; } );
      if ( client1 && client2 ) {
        if ( client1?.name < client2.name ) { return -1; }
        if ( client1?.name > client2.name ) { return 1; }
      }
      if ( site1 && site2 ) {
        if ( site1.name < site2.name ) { return -1; }
        if ( site1.name > site2.name ) { return 1; }
      }
      if ( skill1 && skill2 && skill1.name < skill2.name ) { return -1; }
      if ( skill1 && skill2 && skill1.name > skill2.name ) { return 1; }
      return 0;
    } );

    setSkills( responseSkills );
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const checkClientFilter = (): void => {
    const newFilter = filterClients
      .filter( clientIdFilter => {
        return clients.find( client => { return client.id === clientIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterClients.length ) {
      setFilterClients( newFilter );
    }
  };

  const checkSitesFilter = (): void => {
    const sitesOfClients = sites.filter( site => { return clients.find( client => { return client.id === site.clientId; } ) != null; } );
    const newFilter = filterSites
      .filter( siteIdFilter => {
        return sitesOfClients.find( site => { return site.id === siteIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterSites.length ) {
      setFilterSites( newFilter );
    }
  };

  /*------------------------------ Events -------------------------------*/
  const onChangeFilteredClients = ( clientId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterClients( [ ...filterClients, clientId ] );
    } else {
      const copyFilterClients = [ ...filterClients ];
      const index = copyFilterClients.findIndex( item => { return item === clientId; } );
      if ( index != -1 ) {
        copyFilterClients.splice( index, 1 );
      }
      setFilterClients( copyFilterClients );
    }
  };

  const onChangeFilteredSites = ( siteId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterSites( [ ...filterSites, siteId ] );
    } else {
      const copyFilterSites = [ ...filterSites ];
      const index = copyFilterSites.findIndex( item => { return item === siteId; } );
      if ( index != -1 ) {
        copyFilterSites.splice( index, 1 );
      }
      setFilterSites( copyFilterSites );
    }
  };

  const openExportModal = () => {
    setIsOpenExportModal( true );
  };
  const closeExportModal = () => {
    setIsOpenExportModal( false );
  };

  const openImportModal = () => {
    setIsOpenImportModal( true );
  };
  const closeImportModal = ( isImported?: boolean ) => {
    setIsOpenImportModal( false );
    if ( isImported === true ) {
      refreshSkills();
    }
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ false } />
      <div>
        <div>
          <HomeTopBar title={ t( 'Skill' ) } needConfirmationToNav={ false }/>
        </div>
        <div className='flex h-full gap-1 mx-2'>

          <div className='flex flex-col gap-10 pt-10 w-[250px] h-full'>
            {
              me && ( CommonGlobalService.isClientAdmin( me ) || CommonGlobalService.isSiteAdmin( me ) ) ?
                <div className='flex flex-col w-[250px]'>
                  <Button
                    label={ t( 'New skill' ) }
                    className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                    icon={ <PlusIcon className='w-[24px] h-[24px] p-0.5 fill-white' /> }
                    onClick={ () => { NavigationService.toNewSkill( navigate, false ); } }
                  />
                  {
                    CommonGlobalService.isClientAdmin( me ) ?
                      <div className='flex'>
                        <div className='flex items-center w-full'>
                          <Button
                            label={ t( 'common:export' ) }
                            className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                            icon={ <DownloadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                            onClick={ openExportModal }
                          />
                        </div>
                        <div className='flex items-center'>
                          <Button
                            label={ t( 'common:import' ) }
                            className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                            icon={ <UploadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                            onClick={ openImportModal }
                          />
                          <ImportHelperModal
                            title={ t( 'import_help_title' ) }
                            text={
                              <div className='space-y-2 text-sm text-gray-700'>
                                <p className='font-semibold text-gray-800'>{ t( 'template_columns_intro' ) }</p>
                                <ul className='list-disc pl-5 space-y-1'>
                                  {
                                    Object.values( SKILL_COLUMN )
                                      .map( column => {
                                        return ( <li key={ column }> { t( column ).trim() } </li> );
                                      } )
                                  }
                                </ul>
                              </div>
                            }
                            api={ () => {return SkillService.downloadSkillsTemplate( token, dispatch );} }
                          />
                        </div>
                      </div>
                      :
                      null
                  }
                </div>
                :
                null
            }
            {
              clients.length != 0 ?
                <div className='relative border p-1 rounded-lg text-left h-fit w-full'>
                  <div className='absolute w-fit px-1.5 -top-3.5 left-3 bg-white font-bold'>{ t( 'Client' ) }</div>
                  <div className='px-4 py-2 text-sm'>
                    {
                      <div>
                        {
                          clients.slice( 0, seeMoreClients ? clients.length : 5 ).map( client => {
                            const sitesOfClient = sites.filter( site => { return site.clientId === client.id; } );
                            return (
                              <div key={ client.id } className='flex flex-col mb-1'>
                                <div className='flex gap-2 items-center select-none'>
                                  <input
                                    type='checkbox'
                                    id={ client.id }
                                    className='h-fit cursor-pointer'
                                    checked={ filterClients.find( clientId => { return clientId === client.id; } ) != null }
                                    onChange={ e => { onChangeFilteredClients( client.id, ( e.target as HTMLInputElement ).checked ); } }
                                  />
                                  <label htmlFor={ client.id } className='font-medium cursor-pointer truncate'>{ client.name }</label>
                                  <div
                                    style={{ display: me?.isSuperAdmin == true ? '' : 'none' }}
                                    onClick={ () => {
                                      client.isCollapsed = !client.isCollapsed;
                                      setClients( structuredClone( clients ) );
                                    } }
                                  >
                                    {
                                      sitesOfClient && sitesOfClient.length != 0 ?
                                        <div className='w-[18px] h-[18px] bg-gray-200 p-1 ml-1 rounded-full'>
                                          {
                                            client.isCollapsed === true || GlobalService.clientOrChildrensIsFilter( client, { clients: filterClients, sites: filterSites, sectors: [] } ) ?
                                              <MinusIcon className='w-[12px] -mt-[2px] -ml-[1px]'/>
                                              :
                                              <PlusIcon className='w-[10px]'/>
                                          }
                                        </div>
                                        :
                                        null
                                    }
                                  </div>
                                </div>
                                <div className='ml-1.5'>
                                  {
                                    sitesOfClient && ( client.isCollapsed || filterClients.includes( client.id ) || sitesOfClient.find( site => { return filterSites.includes( site.id ); } ) ) != null ?
                                      sitesOfClient.map( site => {
                                        return (
                                          <div key={ site.id } className='flex flex-col mb-1'>
                                            <div className='flex gap-2 items-center select-none'>
                                              <input
                                                type='checkbox'
                                                id={ site.id }
                                                className='h-fit cursor-pointer'
                                                checked={ filterSites.find( siteId => { return siteId === site.id; } ) != null }
                                                onChange={ e => { onChangeFilteredSites( site.id, ( e.target as HTMLInputElement ).checked ); } }
                                              />
                                              <label htmlFor={ site.id } className='cursor-pointer truncate text-xs'>{ site.name }</label>
                                            </div>
                                          </div>
                                        );
                                      } )
                                      :
                                      null
                                  }
                                </div>
                              </div>
                            );
                          } )
                        }
                        {
                          clients.length > 5 ?
                            <SeeMore
                              isSeeMore={ seeMoreClients }
                              onClick={ () => { setSeeMoreClients( !seeMoreClients ); } }
                              t={t}
                            />
                            :
                            null
                        }
                      </div>
                    }
                  </div>
                </div>
                :
                null
            }
          </div>

          <div className='flex flex-col w-full h-full mx-2 items-center'>
            {
              isFilterEnd ?
                <div className='flex w-1/2 min-w-[400px] gap-2 justify-center items-center'>
                  <SearchInput
                    value={ filterName }
                    onSearched={ value => { setFilterName( value ); } }
                    t={ t }
                  />
                </div>
                :
                null
            }
            {
              !isLoading ?
                <div className='flex flex-col gap-10 justify-center w-full h-full'>
                  {
                    skills.length > 0 ?
                      <div className='flex flex-wrap w-full justify-center'>
                        {
                          skills.map( skill => {
                            const site = sites.find( item => { return item.id === skill.siteId; } );
                            const client = clients.find( item => { return item.id === site?.clientId; } );
                            return (
                              <SkillComponent
                                key={ skill.id }
                                skill={ skill }
                                siteOfSkill={ site }
                                clientOfSite={ client }
                                t={ t }
                              />
                            );
                          } )
                        }
                      </div>
                      :
                      <div className='flex flex-col gap-4 w-full h-full pt-10 items-center justify-center'>
                        <SkillsIcon className='h-24'/>
                        <div className='flex flex-col items-center justify-center'>
                          <span className='font-bold'>{t( 'common:oops' ) }</span>
                          <span className='font-medium'>{ t( 'No skills found !' ) }</span>
                        </div>
                      </div>
                  }

                  <ExportModal
                    title={ t( 'translation:export_skills' ) }
                    isOpen={ isOpenExportModal }
                    onClose={ closeExportModal }
                    exportFunction={ SkillService.exportSkillsOfClient }
                    showSiteFilter={ true }
                    showSectorFilter={ false }
                  />
                  <ImportModal
                    title={ t( 'translation:import_skills' ) }
                    isOpen={ isOpenImportModal }
                    onClose={ closeImportModal }
                    importFunction={ SkillService.importSkillsOnClient}
                  />
                </div>
                :
                <Loading/>
            }
          </div>

        </div>
      </div>
    </>
  );

}